import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Terminal, Volume2, VolumeX, Radio, Cpu, ShieldCheck } from 'lucide-react';

// Institutional boot diagnostics with required tags
export const BOOT_SEQUENCE_STEPS = [
  {
    tag: '[KERNEL]',
    tagColor: 'text-cyan-400 border-cyan-500/30 bg-cyan-950/30',
    text: 'Initializing Titan Neural Core Engine v4.8.2',
    status: '[OK]',
    statusColor: 'text-emerald-400',
  },
  {
    tag: '[CIPHER]',
    tagColor: 'text-fuchsia-400 border-fuchsia-500/30 bg-fuchsia-950/30',
    text: 'Quantum-resistant AES-256-GCM session tunnel established',
    status: '[SECURED]',
    statusColor: 'text-emerald-400',
  },
  {
    tag: '[QUANT_ORACLE]',
    tagColor: 'text-amber-400 border-amber-500/30 bg-amber-950/30',
    text: 'Connecting Binance/Bybit WebSocket multi-feeds (2,418 ACTIVE NODES)',
    status: '[SYNC]',
    statusColor: 'text-cyan-400',
  },
  {
    tag: '[SHIELD]',
    tagColor: 'text-cyan-400 border-cyan-500/30 bg-cyan-950/30',
    text: 'Engaging Anti-DDoS rate-limiter & IP quarantine mesh (14ms latency)',
    status: '[ACTIVE]',
    statusColor: 'text-emerald-400',
  },
  {
    tag: '[NEURAL_GATEWAY]',
    tagColor: 'text-emerald-400 border-emerald-500/30 bg-emerald-950/30',
    text: 'Loading AI Market Copilot & Sentiment Radar models',
    status: '[ONLINE]',
    statusColor: 'text-emerald-400',
  },
  {
    tag: '[AI_PROXY]',
    tagColor: 'text-fuchsia-400 border-fuchsia-500/30 bg-fuchsia-950/30',
    text: 'AI Gateway Reverse Proxy & Mock Sandbox verified',
    status: '[READY]',
    statusColor: 'text-cyan-400',
  },
  {
    tag: '[COMPLIANCE]',
    tagColor: 'text-amber-400 border-amber-500/30 bg-amber-950/30',
    text: 'Regulatory Risk Assessment & Margin Protocol certified',
    status: '[VERIFIED]',
    statusColor: 'text-amber-400',
  },
  {
    tag: '[AUTH_MATRIX]',
    tagColor: 'text-emerald-400 border-emerald-500/30 bg-emerald-950/30',
    text: 'Decryption complete. Terminal access granted',
    status: '[SUCCESS]',
    statusColor: 'text-emerald-300 font-bold',
  },
];

// Helper to format real-time ISO-based terminal timestamp [HH:MM:SS.mmm]
export const getFormattedTimestamp = () => {
  const d = new Date();
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `[${h}:${m}:${s}.${ms}]`;
};

// Web Audio API Synthesizer (Zero asset dependency, never fails 404)
const playAudioFx = (type, isMuted) => {
  if (isMuted || typeof window === 'undefined') return;
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    if (type === 'blip') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1080, ctx.currentTime);
      gain.gain.setValueAtTime(0.03, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.04);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.04);
    } else if (type === 'complete') {
      [523.25, 659.25, 783.99, 1046.50].forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.06);
        gain.gain.setValueAtTime(0.04, ctx.currentTime + idx * 0.06);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + idx * 0.06 + 0.12);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + idx * 0.06);
        osc.stop(ctx.currentTime + idx * 0.06 + 0.12);
      });
    }
  } catch {
    // Graceful fallback if audio permissions restricted
  }
};

export default function BootTerminal({
  onComplete,
  autoStart = true,
  soundDefault = false,
  nodeCount = '2,418 ACTIVE NODES',
  pingLatency = '14ms',
}) {
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(soundDefault);

  const isSkippedRef = useRef(false);
  const timerRef = useRef(null);
  const logContainerRef = useRef(null);
  const stepIndexRef = useRef(0);

  // Trigger aperture scale-and-blur transition and exit
  const triggerExit = useCallback(() => {
    if (isSkippedRef.current) return;
    isSkippedRef.current = true;

    // Check for reduced motion preference safely
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      Boolean(window.matchMedia('(prefers-reduced-motion: reduce)')?.matches);

    setProgress(100);
    playAudioFx('complete', !soundEnabled);

    if (prefersReducedMotion) {
      if (onComplete) onComplete();
      return;
    }

    setIsExiting(true);
    const exitTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 420); // Syncs with 420ms aperture scale-and-blur transition

    return () => clearTimeout(exitTimer);
  }, [onComplete, soundEnabled]);

  // Skip handler (idempotent)
  const handleSkip = useCallback(() => {
    if (isSkippedRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);

    // Fast-forward remaining logs
    const remainingLogs = BOOT_SEQUENCE_STEPS.map((step) => ({
      ...step,
      timestamp: getFormattedTimestamp(),
    }));
    setLogs(remainingLogs);
    triggerExit();
  }, [triggerExit]);

  // Global keydown listener: Enter, Escape, Space
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === 'Escape' || e.key === ' ') {
        e.preventDefault();
        handleSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSkip]);

  // Step-by-step sequential boot stream
  useEffect(() => {
    if (!autoStart) return;

    const streamNextStep = () => {
      if (isSkippedRef.current) return;

      const currentIdx = stepIndexRef.current;
      if (currentIdx < BOOT_SEQUENCE_STEPS.length) {
        const step = BOOT_SEQUENCE_STEPS[currentIdx];
        const newLog = {
          ...step,
          timestamp: getFormattedTimestamp(),
        };

        setLogs((prev) => [...prev, newLog]);
        playAudioFx('blip', !soundEnabled);

        const currentPct = Math.round(((currentIdx + 1) / BOOT_SEQUENCE_STEPS.length) * 100);
        setProgress(currentPct);

        stepIndexRef.current += 1;

        // Auto-scroll logs to bottom
        if (logContainerRef.current) {
          logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }

        // Realistic cybernetic terminal delay (180ms per step)
        const stepDelay = 180;
        timerRef.current = setTimeout(streamNextStep, stepDelay);
      } else {
        // Complete with deliberate pause for inspection
        timerRef.current = setTimeout(() => {
          triggerExit();
        }, 300);
      }
    };

    timerRef.current = setTimeout(streamNextStep, 120);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [autoStart, soundEnabled, triggerExit]);

  // Scroll to bottom whenever logs update
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  return (
    <div
      role="dialog"
      aria-label="System Boot Terminal"
      aria-modal="true"
      className={`fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-[#05070c]/95 backdrop-blur-2xl transition-all duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
        isExiting
          ? 'scale-[0.96] blur-[8px] opacity-0 pointer-events-none'
          : 'scale-100 blur-0 opacity-100'
      }`}
    >
      {/* Ambient background glow accents */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Cyberpunk Terminal Window Frame */}
      <div className="relative w-full max-w-3xl rounded-xl border border-cyan-500/30 bg-[#0a0e17]/90 shadow-[0_0_50px_rgba(0,240,255,0.12),inset_0_1px_1px_rgba(255,255,255,0.1)] overflow-hidden font-mono text-xs sm:text-sm">
        
        {/* Decorative Cyber Reticles / Corner Brackets */}
        <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-cyan-400 pointer-events-none" />
        <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-cyan-400 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-fuchsia-400 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-fuchsia-400 pointer-events-none" />

        {/* Top Header & Telemetry Bar */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-cyan-500/20 bg-slate-950/60 select-none">
          {/* Brand & System Title */}
          <div className="flex items-center space-x-2.5">
            <div className="relative flex items-center justify-center w-6 h-6 rounded bg-cyan-950/80 border border-cyan-400/40 text-cyan-400">
              <Terminal size={14} />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="font-bold tracking-wider text-slate-200">WIN AI TRADE // QUANTUM OS</span>
                <span className="px-1.5 py-0.2 text-[10px] rounded bg-cyan-950 border border-cyan-500/40 text-cyan-400">
                  BOOT_KERNEL_v4.8.2
                </span>
              </div>
            </div>
          </div>

          {/* Right Header Controls: Telemetry, Audio Pulse, and Skip Button */}
          <div className="flex items-center space-x-2 sm:space-x-3 text-[11px]">
            {/* Real-time Ping Latency */}
            <div className="hidden sm:flex items-center space-x-1.5 px-2 py-1 rounded bg-slate-900/80 border border-white/10 text-slate-300">
              <Radio size={12} className="text-emerald-400 animate-pulse" />
              <span className="text-slate-400">PING:</span>
              <span className="text-emerald-400 font-semibold">{pingLatency}</span>
            </div>

            {/* Active Nodes */}
            <div className="hidden md:flex items-center space-x-1.5 px-2 py-1 rounded bg-slate-900/80 border border-white/10 text-slate-300">
              <Cpu size={12} className="text-cyan-400" />
              <span className="text-cyan-400 font-semibold">{nodeCount}</span>
            </div>

            {/* Visual Sound-Wave Pulse Indicator & Sound Toggle */}
            <button
              type="button"
              onClick={() => setSoundEnabled((prev) => !prev)}
              title={soundEnabled ? 'Sound FX Enabled (Click to Mute)' : 'Sound FX Muted (Click to Enable)'}
              aria-label={soundEnabled ? 'Mute sound effects' : 'Enable sound effects'}
              className="flex items-center space-x-1 px-2 py-1 rounded bg-slate-900/80 border border-cyan-500/30 hover:border-cyan-400 text-slate-300 transition-colors cursor-pointer"
            >
              {/* Dynamic 5-bar sound equalizer */}
              <div className="flex items-center space-x-0.5 h-3.5 px-0.5" aria-hidden="true">
                <span className={`w-0.5 bg-cyan-400 rounded-full transition-all ${soundEnabled ? 'animate-bounce h-3' : 'h-1.5 opacity-50'}`} />
                <span className={`w-0.5 bg-cyan-300 rounded-full transition-all ${soundEnabled ? 'animate-bounce delay-75 h-3.5' : 'h-2 opacity-50'}`} />
                <span className={`w-0.5 bg-fuchsia-400 rounded-full transition-all ${soundEnabled ? 'animate-bounce delay-150 h-2' : 'h-1.5 opacity-50'}`} />
                <span className={`w-0.5 bg-fuchsia-500 rounded-full transition-all ${soundEnabled ? 'animate-bounce delay-100 h-3' : 'h-2 opacity-50'}`} />
                <span className={`w-0.5 bg-emerald-400 rounded-full transition-all ${soundEnabled ? 'animate-bounce delay-200 h-1.5' : 'h-1 opacity-50'}`} />
              </div>
              {soundEnabled ? (
                <Volume2 size={13} className="text-cyan-400 ml-1" />
              ) : (
                <VolumeX size={13} className="text-slate-500 ml-1" />
              )}
            </button>

            {/* Interactive Skip Button */}
            <button
              type="button"
              onClick={handleSkip}
              className="px-2.5 py-1 rounded border border-cyan-400/50 bg-cyan-950/40 text-cyan-300 hover:bg-cyan-500/20 hover:border-cyan-300 hover:text-white transition-all shadow-[0_0_10px_rgba(0,240,255,0.15)] active:scale-95 font-semibold cursor-pointer"
            >
              Skip (Enter/Esc)
            </button>
          </div>
        </div>

        {/* Terminal Log Console */}
        <div
          ref={logContainerRef}
          role="log"
          aria-live="polite"
          className="p-4 sm:p-5 h-64 sm:h-72 overflow-y-auto space-y-2 bg-[#05070c]/80 text-slate-300 scrollbar-thin scrollbar-thumb-cyan-500/20"
        >
          {logs.map((log, index) => (
            <div
              key={index}
              className="flex items-start space-x-2 leading-relaxed tracking-wide animate-fade-in"
            >
              {/* Timestamp */}
              <span className="text-slate-500 shrink-0 text-[11px] sm:text-xs">
                {log.timestamp}
              </span>

              {/* Module Tag */}
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] sm:text-[11px] font-semibold border ${log.tagColor} shrink-0`}
              >
                {log.tag}
              </span>

              {/* Message */}
              <span className="flex-1 text-slate-300 break-words">
                {log.text}
              </span>

              {/* Status Badge */}
              <span className={`font-semibold shrink-0 ml-1 ${log.statusColor}`}>
                {log.status}
              </span>
            </div>
          ))}

          {/* Active Terminal Typewriter Cursor */}
          {progress < 100 && (
            <div className="flex items-center space-x-2 text-cyan-400 text-xs sm:text-sm pt-1">
              <span className="text-slate-500">{getFormattedTimestamp()}</span>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold border border-cyan-500/30 bg-cyan-950/30 text-cyan-400">
                [DAEMON]
              </span>
              <span className="text-slate-400">Synchronizing neural telemetry layers</span>
              <span className="w-2 h-4 bg-cyan-400 animate-pulse inline-block" />
            </div>
          )}
        </div>

        {/* Dynamic Animated Progress Bar & Bottom HUD */}
        <div className="p-3 sm:p-4 border-t border-cyan-500/20 bg-slate-950/80">
          {/* Progress Bar Container */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-[11px] text-slate-400">
              <div className="flex items-center space-x-2">
                <ShieldCheck size={13} className="text-emerald-400" />
                <span className="text-cyan-300 font-semibold">
                  QUANTUM BOOT SEQUENCE: {progress}%
                </span>
              </div>
              <span className="text-slate-500 hidden sm:inline">
                PRESS [ENTER], [ESC] OR [SPACE] TO BYPASS
              </span>
            </div>

            {/* Glowing Gradient Track */}
            <div className="relative h-2 w-full bg-slate-900 rounded-full overflow-hidden border border-white/10">
              <div
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin="0"
                aria-valuemax="100"
                style={{ width: `${progress}%` }}
                className="h-full bg-gradient-to-r from-cyan-400 via-blue-500 to-fuchsia-500 rounded-full transition-all duration-200 ease-out shadow-[0_0_15px_rgba(0,240,255,0.7)]"
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export { BootTerminal };
